/**
 * Flattening + price formatting for the product cards a transcript carries.
 *
 * Ported from the widget's app/src/platforms/shared/card-classifier.ts (the
 * product branch of classifyCards) so the backoffice reads the exact same wire
 * shape the shopper's widget read. The backoffice receives the RAW Chatwoot
 * card item inside blocks[].payload.items, not the flattened shape, so the
 * flattening has to happen here too.
 *
 * Why the structured fields hide inside an action: Chatwoot's
 * ContentAttributeValidator allow-lists card item keys to
 * [title, description, media_url, actions] and drops everything else, but it
 * passes actions[].payload through unvalidated. So product_id, price,
 * original_price, original_price_kind, delivery, badges, images and
 * availability all travel inside the `link` action's payload hash.
 *
 * Pure: no network, no component state, same input gives the same output.
 */

import { parsePageUrl } from "./contextAttributes";

// Shape-check every enrichment field, the same defensive stance the widget
// takes: a malformed payload degrades to "element absent", never to a broken
// card on an operator's screen.
function normalizeDelivery(raw) {
  if (
    !raw ||
    typeof raw !== "object" ||
    typeof raw.min_days !== "number" ||
    typeof raw.max_days !== "number" ||
    typeof raw.business_days !== "boolean"
  ) {
    return undefined;
  }
  return {
    min_days: raw.min_days,
    max_days: raw.max_days,
    // false = CALENDAR days, and the range is an upper bound: the renderer
    // must say "ate N dias", never "N dias" flat (RPAA-196 false absolutes).
    business_days: raw.business_days,
  };
}

function normalizeBadges(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (
      !entry ||
      typeof entry !== "object" ||
      typeof entry.kind !== "string" ||
      typeof entry.label !== "string"
    ) {
      return [];
    }
    return [
      {
        // Open string, not a fixed set: known kinds today are "discount",
        // "discount_pvpr" and "campaign", but a tenant can introduce new ones
        // with zero code changes and they render with the default badge style.
        kind: entry.kind,
        label: entry.label,
        ...(typeof entry.valid_until === "string"
          ? { valid_until: entry.valid_until }
          : {}),
      },
    ];
  });
}

function normalizeImages(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((u) => typeof u === "string" && u.length > 0);
}

// Texto do payload do tenant, aceite so se for mesmo texto com conteudo. O
// resto do ficheiro trata este payload como nao validado (ver o `badges[].kind`
// e o `link.uri`), e um numero ou um objeto aqui desenhava "[object Object]" na
// transcricao.
function normalizeText(raw) {
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : undefined;
}

function normalizeSizes(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeText).filter((v) => v !== undefined);
}

function linkActionOf(item) {
  const actions = Array.isArray(item.actions) ? item.actions : [];
  return actions.find((a) => a && a.type === "link");
}

/**
 * Qual dos quatro escritores de cartoes mandou este lote: "product",
 * "curated", "choice" ou "end_conversation" (a oferta do botao de terminar
 * conversa, RPAA-231, que e um item sem titulo, sem preco e sem link).
 *
 * Mesmo discriminador do widget (classifyCards em
 * app/src/platforms/shared/card-classifier.ts): o `template` vive no payload da
 * accao `link` de cada item, e a decisao e tudo-ou-nada. Um lote misturado, que
 * so um bug a montante produz, cai no ramo dos produtos, para os itens que sao
 * mesmo produtos nao perderem o preco e o link em vez de serem desenhados como
 * cartoes em branco.
 *
 * Um bloco sem itens tambem da "product", tal como no widget: e o unico ramo
 * que nao promete um desenho, e nao ha ali nada para desenhar.
 */
export function cardsTemplate(items) {
  if (!Array.isArray(items) || items.length === 0) return "product";
  const templates = items.map((item) => {
    const link = linkActionOf(item || {});
    return ((link && link.payload) || {}).template;
  });
  if (templates.every((t) => t === "curated")) return "curated";
  if (templates.every((t) => t === "choice")) return "choice";
  if (templates.every((t) => t === "end_conversation")) return "end_conversation";
  return "product";
}

/**
 * One raw Chatwoot card item to the flat shape the card component consumes.
 *
 * Deliberately DOES NOT produce `buy_button_active`. The widget derives that
 * flag from the presence of a `postback` action and uses it to show the add to
 * cart button; the backoffice is read only, so the flag, the postback action
 * and everything downstream of them are dropped here rather than carried and
 * ignored.
 */
function flattenProductCardItem(item) {
  const raw = item || {};
  const link = linkActionOf(raw);
  const payload = (link && link.payload) || {};
  // O destino do cartao passa pelo mesmo parsePageUrl que o URL da pagina do
  // visitante, no painel do contacto: o `link.uri` chega no mesmo payload nao
  // validado que o `badges[].kind` aqui em cima, e aceitar qualquer string
  // punha um `javascript:` num href da sessao autenticada do operador. O React
  // 18 nao tapa isso, so avisa, e so em desenvolvimento. Fora de http e https o
  // campo nao nasce e o cartao desenha-se como texto inerte, que e o ramo que o
  // componente ja tem para um cartao sem link. Um URL relativo tambem cai: ele
  // seria resolvido contra o backoffice e nao contra a loja, portanto era um
  // link partido de qualquer maneira.
  const cta = link ? parsePageUrl(link.uri) : null;
  const delivery = normalizeDelivery(payload.delivery);
  const badges = normalizeBadges(payload.badges);
  const images = normalizeImages(payload.images);
  const sizes = normalizeSizes(payload.sizes);
  const availability =
    typeof payload.availability === "string" && payload.availability !== ""
      ? payload.availability
      : undefined;
  return {
    product_id: payload.product_id,
    title: raw.title,
    price: payload.price,
    original_price: payload.original_price,
    // "recommended" = a manufacturer PVPR, labelled and NEVER struck through.
    // "previous" (or absent, for cards emitted before the field existed) = a
    // genuine earlier price, struck through. Collapsing the two misrepresents
    // a reference price as a reduction, which is a tracked bug class here.
    original_price_kind: payload.original_price_kind,
    // A etiqueta da referencia e copy DO TENANT, e por isso e que existe: o
    // MODZ manda "Prix boutique :", que nenhuma chave de traducao nossa diz. O
    // componente usa-a a frente do valor e deixa a semantica (riscado ou nao)
    // com o `original_price_kind`, tal como o widget.
    ...(normalizeText(payload.original_price_label)
      ? { original_price_label: normalizeText(payload.original_price_label) }
      : {}),
    // A marca e os tamanhos sao as duas linhas que o cartao do widget tem e
    // este nao tinha. Chegam neste mesmo payload desde sempre, eram e que eram
    // deitadas fora aqui, portanto uma transcricao antiga volta a mostra-las.
    ...(normalizeText(payload.brand) ? { brand: normalizeText(payload.brand) } : {}),
    image_url: raw.media_url,
    ...(cta ? { cta_url: cta.href } : {}),
    ...(delivery ? { delivery } : {}),
    ...(badges.length > 0 ? { badges } : {}),
    // > 1 only, mirroring the backend and the widget: a lone image is already
    // media_url, there is nothing to swap to.
    ...(images.length > 1 ? { images } : {}),
    ...(availability ? { availability } : {}),
    ...(sizes.length > 0 ? { sizes } : {}),
  };
}

export function flattenProductCardItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map(flattenProductCardItem);
}

// Europe/Lisbon "wall clock now" in the same naive ISO convention the backend
// uses for badges[].valid_until ("2026-12-31T23:55:00", no timezone
// designator). The badge timestamp is NEVER parsed: new Date() on a naive
// string reads it as the BROWSER's local wall time, wrong for any operator
// outside Portugal and DST fragile inside it. Formatting "now" into the
// badge's own convention lets us compare with plain string ordering, since
// zero padded ISO strings sort chronologically.
let lisbonFormatter;

function nowInLisbonNaiveIso(now = new Date()) {
  // Built once, lazily: the Intl constructor dominates the call cost and this
  // runs once per card per render. Lazy rather than module eager so an
  // environment missing Lisbon tz data fails at first use, not at import.
  // Locale en-CA is decorative, the output is reassembled part by part, so
  // only hourCycle is load bearing.
  if (!lisbonFormatter) {
    lisbonFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Lisbon",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
  }
  const parts = lisbonFormatter.formatToParts(now);
  const get = (type) => {
    const part = parts.find((p) => p.type === type);
    return part ? part.value : "00";
  };
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get(
    "minute"
  )}:${get("second")}`;
}

/**
 * Badges still valid right now, in Lisbon wall time.
 *
 * Expiry is re evaluated at RENDER time, not at flatten time: the payload can
 * sit 24h in the backend card cache and forever in the conversation history,
 * so a transcript opened months later must not show a campaign that had
 * already ended. A badge with no valid_until never expires.
 */
export function activeBadges(badges) {
  if (!Array.isArray(badges) || badges.length === 0) return [];
  const nowLisbon = nowInLisbonNaiveIso();
  return badges.filter(
    (b) => b.valid_until === undefined || b.valid_until >= nowLisbon
  );
}

// A bare number the way Python stringifies a float: no thousands separator, a `.` decimal,
// and nothing at all after the euros when the price is whole. That is what the backend used
// to send, so `€99999` reached the card verbatim for the €99.999,00 TV HISENSE MX 136 (seen
// live 2026-08-06). The backend formats its own prices now, but every card already sent is
// stored forever in the conversation history, so those only heal if the client does it.
//
// Deliberately narrow: a value carrying a `,`, or grouped, or with 3+ trailing decimals, is
// already formatted and must be left alone. Anything else ("Gratis", "A consulta") has no
// digits to match and falls through.
const BARE_AMOUNT = /^(\D*)(\d+)(?:\.(\d{1,2}))?(\D*)$/;

// Always returns a string, including on the pass-through path. The widget's
// version is typed `(value: string) => string`; here the payload is untyped, so
// a numeric price would otherwise return a number and crash the caller's
// .match() on the way to the cents superscript.
export function toPtAmount(value) {
  const text = String(value);
  const match = text.match(BARE_AMOUNT);
  if (!match) return text;
  const prefix = match[1] || "";
  const euros = match[2] || "";
  const cents = match[3];
  const suffix = match[4] || "";
  const grouped = euros.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  // padEnd, not padStart: in `12.5` the 5 is five tenths of a euro, so 50 cents.
  return `${prefix}${grouped},${(cents || "").padEnd(2, "0")}${suffix}`;
}
